import { ShipToStoreCartonLabel, ShipToStoreCartonLabelStatus } from "@deliverr/sortation-client";
import { createDangerNotification, log, logError, logStart } from "@deliverr/ui-facility";
import { useCommonFlow } from "@deliverr/ui-facility/lib-facility/flow/useCommonFlow";
import { useIntl } from "react-intl";
import { isNil } from "lodash/fp";
import { desktopPrinterState } from "sortation/base/BrowserPrint";
import { useSortationClient } from "sortation/base/useSortationClient";
import { useCallback, useState } from "react";
import { useAsync } from "react-use";
import { useRecoilValue } from "recoil";
import { sortationIdState } from "sortation/base/sortationIdState";
import { useSortationPrint } from "sortation/base/useSortationPrint";
import { TOAST_AUTO_CLOSE } from "sortation/utils/toastAutoClose";

export interface PrintJobSummary {
  totalLabels: number;
  successCount: number;
  failedCount: number;
}

export const useShipToStoreLabel = () => {
  const sortationId = useRecoilValue(sortationIdState);
  const sortationClient = useSortationClient();
  const [shipToStoreLabels, setShipToStoreLabels] = useState<ShipToStoreCartonLabel[]>([]);
  const { addAutoCloseNotification } = useCommonFlow();
  const { print } = useSortationPrint();
  const hasDesktopPrinter = useRecoilValue(desktopPrinterState);
  const { formatMessage } = useIntl();
  const [isPrinting, setIsPrinting] = useState(false);
  const [printSummary, setPrintSummary] = useState<PrintJobSummary>({
    totalLabels: 0,
    successCount: 0,
    failedCount: 0,
  });
  // Initial data fetch
  const { loading: isLoading } = useAsync(async () => {
    try {
      if (!sortationId) {
        return;
      }
      const labels = await sortationClient.getShipToStoreLabels(sortationId);
      setShipToStoreLabels(labels);
    } catch (err) {}
  }, [sortationId]);

  const onRefetch = useCallback(async () => {
    if (!sortationId) {
      return;
    }
    const labels = await sortationClient.getShipToStoreLabels(sortationId);
    setShipToStoreLabels(labels);
  }, [sortationClient, sortationId]);

  const printLabels = useCallback(
    async (labels: ShipToStoreCartonLabel[]) => {
      const ctx = logStart({ fn: "ShipToStoreLabel.printLabels", numberOfLabels: labels.length });
      const printedLabelIds: number[] = [];
      const failedLabelIds: number[] = [];

      // TODO: add label print max limit

      setIsPrinting(true);

      try {
        const labelIds = labels.map((label) => label.id);
        const labelZpls = await sortationClient.getShipToStoreLabelZpls(labelIds);
        log(ctx, "retrieved label zpls");

        for (const label of labelZpls) {
          try {
            const formattedZpl = atob(label.zpl);
            await print(formattedZpl);
            printedLabelIds.push(label.labelId);
          } catch (err) {
            failedLabelIds.push(label.labelId);
            logError(ctx, err);
          }
        }

        await Promise.all([
          sortationClient.updateShipToStoreLabelStatuses(printedLabelIds, ShipToStoreCartonLabelStatus.PRINTED),
          sortationClient.updateShipToStoreLabelStatuses(failedLabelIds, ShipToStoreCartonLabelStatus.PRINT_FAILED),
        ]);

        setPrintSummary({
          totalLabels: labels.length,
          successCount: printedLabelIds.length,
          failedCount: failedLabelIds.length,
        });
      } catch (err) {
        logError(ctx, err);
        addAutoCloseNotification(
          createDangerNotification(
            formatMessage({
              id: "sortation.shipToStorePrint.printLabelError",
              defaultMessage: "Something went wrong, please try again.",
            })
          ),
          TOAST_AUTO_CLOSE
        );
      } finally {
        setIsPrinting(false);
      }
    },
    [formatMessage, addAutoCloseNotification, print, setPrintSummary, sortationClient]
  );

  const cancelLabels = useCallback(
    async (labels: ShipToStoreCartonLabel[]) => {
      const ctx = logStart({ fn: "ShipToStoreLabel.cancelLabels", numberOfLabels: labels.length });

      try {
        const labelIds = labels.map((label) => label.id);
        await sortationClient.updateShipToStoreLabelStatuses(labelIds, ShipToStoreCartonLabelStatus.CANCELED);
      } catch (err) {
        logError(ctx, err);
        addAutoCloseNotification(
          createDangerNotification(
            formatMessage({
              id: "sortation.shipToStorePrint.cancelLabelError",
              defaultMessage: "Something went wrong, please try again.",
            })
          ),
          TOAST_AUTO_CLOSE
        );
      }
    },
    [formatMessage, addAutoCloseNotification, sortationClient]
  );
  return {
    shipToStoreLabels,
    isLoading,
    isPrinting,
    printSummary,
    printLabels,
    cancelLabels,
    onRefetch,
    sortationId,
    hasConnectedPrinter: !isNil(hasDesktopPrinter),
  };
};
